import { connect } from "react-redux";
import { Link } from "react-router";
import { DebounceInput } from "react-debounce-input";
import React, { useCallback, useEffect, useRef, useState } from "react";

import {
  useSpinner,
  ExpandableTable,
  ExportButton,
  Spinner,
  compactNumberFormat,
} from "../../../../../shared";
import {
  loginConstants,
  performanceConstants,
  reportTypesConstants,
} from "../../../constants";
import { setItemToStorage } from "../../../../../system";
import SankeyChart from "../../../../../shared/components/chart/sankeyChart";
import { getInboxReportTableColumns } from "../utility";
import InboxConversationExpandedComponent from "../inboxConversationExpandedComponent";
import { performanceActions } from "../../../redux/actions";

const {
  GET_INBOX_CONVERSATION_DATA,
  GET_INBOX_CONVERSATION_DATA_EXPORT,
  GET_INBOX_CONVERSATION_SUMMARY_DATA,
} = performanceConstants;

const allSpinnersConstants = [
  GET_INBOX_CONVERSATION_DATA,
  GET_INBOX_CONVERSATION_SUMMARY_DATA,
  GET_INBOX_CONVERSATION_DATA_EXPORT,
];

const InboxConversationTab = (props) => {
  const {
    getData,
    exportFunc,
    createDateTo,
    createDateFrom,
    inboxTabSearch,
    resetStoresList,
    setInboxTabSearch,
    selectedInboxFilter,
    inboxConversationData,
    resetInboxReportReducer,
    inboxConversationSummary,
    inboxConversationDataPagination,
    getInboxConversationDataByBrandId,
  } = props;

  const [queryString, setQueryString] = useState({
    from: 0,
    take: 15,
  });
  const [expandedRowId, setExpandedRowId] = useState(null);

  //Show spinner until data is fetched
  const spinner = useSpinner();

  //Check if any spinner is active
  const isSpinnerActive = () =>
    allSpinnersConstants.some((spinnerType) => spinner(spinnerType));

  const cellNameRenderer = useCallback(
    (row) => {
      return (
        <Link
          className="table-links"
          to={`/stores/brand-profile/${row?.brandId}`}
          onClick={() => {
            setItemToStorage(loginConstants.BRAND_NAME, row?.brandName);
            setItemToStorage(loginConstants.BRAND_ID, row?.brandId);
          }}
        >
          {row?.brandName}
        </Link>
      );
    },
    // eslint-disable-next-line
    []
  );

  const onRowExpandToggled = (isExpanded, row) => {
    resetStoresList();
    if (isExpanded) {
      setExpandedRowId(row?.brandId);
      getInboxReportByBrandId(row?.brandId);
    } else {
      setExpandedRowId(null);
      setQueryString({
        from: 0,
        take: 15,
      });
    }
  };

  const getInboxReportByBrandId = useCallback(
    (brandId) => {
      getInboxConversationDataByBrandId(
        createDateFrom,
        createDateTo,
        brandId,
        queryString.from,
        queryString.take
      );
    },
    [
      createDateFrom,
      createDateTo,
      queryString.from,
      queryString.take,
      getInboxConversationDataByBrandId,
    ]
  );

  const MemoizedExpandedRowWrapper = React.memo(
    ({ data, createDateTo, queryString, createDateFrom }) => (
      <InboxConversationExpandedComponent
        rowData={data}
        queryString={queryString}
        createDateTo={createDateTo}
        createDateFrom={createDateFrom}
      />
    )
  );

  //Stable wrapper component defined once per render of InboxConversationTab
  const ExpandedRowWrapper = useCallback(
    ({ data }) => (
      <MemoizedExpandedRowWrapper
        data={data}
        queryString={queryString}
        createDateTo={createDateTo}
        createDateFrom={createDateFrom}
      />
    ),
    [createDateTo, createDateFrom, queryString]
  );

  const renderItemIfValueExist = (data) => data?.value > 0;

  const initialRender = useRef(true);
  useEffect(() => {
    //Stop useEffect default execution on component render
    if (initialRender.current) {
      initialRender.current = false;
      return;
    }
    getData(0, inboxConversationDataPagination?.size, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inboxTabSearch]);

  useEffect(() => {
    setExpandedRowId(null);
    getData(0, inboxConversationDataPagination?.size, true);
    return () => {
      resetInboxReportReducer();
      resetStoresList();
      setExpandedRowId(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createDateFrom, createDateTo, selectedInboxFilter]);

  return (
    <>
      {isSpinnerActive() && <Spinner />}
      <div className="container-fluid pt-2">
        <div className="notification-form card shadow mb-2">
          <div className="card-body px-0 position-relative">
            <div className="position-absolute top-0 start-0 border shadow-sm rounded-2 my-2 ms-4 p-2 text-center">
              <h6 className="mb-0 fw-medium">Total Conversation</h6>
              <h3 className="fw-bolder mb-0">
                {compactNumberFormat(
                  inboxConversationSummary?.totalConversationCount.value
                )}
              </h3>
            </div>
            <SankeyChart
              renderItemIfValueExist={renderItemIfValueExist}
              surveyConversionRateKpiSummary={inboxConversationSummary}
              reportType={reportTypesConstants.INBOX_REPORT}
            />
            <div className="sankey-chart-labels">
              {inboxConversationSummary?.totalConversationCount?.value > 0 && (
                <div className="d-flex flex-column align-items-center justify-content-center">
                  <h6 className="fw-bold">Total Conversation</h6>
                </div>
              )}
              {(renderItemIfValueExist(
                inboxConversationSummary?.totalConversationNotStartedCount
              ) ||
                renderItemIfValueExist(
                  inboxConversationSummary?.totalConversationStartedCount
                )) && (
                <div className="d-flex flex-column align-items-center justify-content-center">
                  <h6 className="fw-bold">
                    Conversation (Started/Not Started)
                  </h6>
                </div>
              )}
              {(renderItemIfValueExist(
                inboxConversationSummary?.totalConsumerRespondedCount
              ) ||
                renderItemIfValueExist(
                  inboxConversationSummary?.totalConsumerNotRespondedCount
                )) && (
                <div className="d-flex flex-column align-items-center justify-content-center">
                  <h6 className="fw-bold">
                    Consumer (Responded/Not Responded)
                  </h6>
                </div>
              )}
            </div>
          </div>
        </div>
        <ExportButton
          exportFunc={exportFunc}
          isDisabled={inboxConversationData?.length === 0}
        />
        <div className="notification-form card shadow mb-5">
          <div className="card-body px-0">
            <div className="row">
              <div className="col-6 py-1">
                <h5 className="responses ps-4 m-0">Brand Conversations</h5>
              </div>
              <div className="col-6 py-1">
                <div className="search-container ms-auto me-4">
                  <DebounceInput
                    type="text"
                    value={inboxTabSearch}
                    debounceTimeout={1000}
                    placeholder="Search ..."
                    id="search-store-locations"
                    className="search-restaurants"
                    onChange={(e) => setInboxTabSearch(e.target.value)}
                  />
                  <i aria-hidden="true" className="fa fa-search search-icon" />
                </div>
              </div>
            </div>
            <div className="mt-3">
              <ExpandableTable
                expandOnRowClicked
                handlePageChange={(pageNum) => {
                  getData(pageNum);
                  setExpandedRowId(null);
                }}
                enableServerSorting={true}
                data={inboxConversationData}
                expandableRows
                onRowExpandToggled={onRowExpandToggled}
                expandRowRenderer={ExpandedRowWrapper}
                columns={getInboxReportTableColumns(cellNameRenderer)}
                pageNumber={inboxConversationDataPagination?.number + 1}
                totalRows={inboxConversationDataPagination?.totalElements}
                numberOfRowsPerPage={inboxConversationDataPagination?.size}
                //Only expand the row whose brandId matches expandedRowId
                expandableRowExpanded={(row) => row?.brandId === expandedRowId}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

//Mapping the component's props to the reducer's state
const mapStateToProps = (state) => ({
  inboxConversationDataPagination:
    state.performanceReducer.inboxConversationDataPagination,
  inboxConversationSummary: state.performanceReducer.inboxConversationSummary,
  inboxConversationData: state.performanceReducer.inboxConversationData,
});

//Mapping the component's props to the related actions
const mapDispatchToProps = {
  getInboxConversationDataByBrandId:
    performanceActions.getInboxConversationDataByBrandId,
  resetStoresList: performanceActions.resetStoresList,
  resetInboxReportReducer: performanceActions.resetInboxReportReducer,
};

//Mapping action and store the function via props
export default connect(
  mapStateToProps,
  mapDispatchToProps
)(React.memo(InboxConversationTab));
